import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Input } from 'antd';
import { EnvironmentOutlined, AimOutlined } from '@ant-design/icons';
import { locateBrowser, reverseMapAddress, searchMapPlaces } from '@/utils/amap';
import { coordinatePair, positionFields, wgs84ToGcj02, gcj02ToWgs84 } from '@/utils/coordinates';
import MapCanvas from './MapCanvas';
import ActivityLocationPicker from './ActivityLocationPicker';
import './index.scss';

const InlineLocationPicker = ({ id, longitude, latitude, location, onLocationChange, onChange, posts, heatmap, disabled = false, onBusyChange, feedback, guidance, showMap = true, autoLocate = true }) => {
  const [keyword, setKeyword] = useState(''); const [places, setPlaces] = useState([]);
  const [busy, setBusy] = useState(''); const [error, setError] = useState(''); const [searched, setSearched] = useState(false);
  const [currentPosition, setCurrentPosition] = useState(null);
  const version = useRef(0); const mounted = useRef(true); const changeRef = useRef(onChange); const busyRef = useRef(onBusyChange);
  const fieldsRef = useRef(null); const autoLocateRef = useRef(null);
  const mapShownRef = useRef(showMap);
  changeRef.current = onChange; busyRef.current = onBusyChange;
  const point = coordinatePair(longitude, latitude);
  fieldsRef.current = { point, location, disabled };
  const editableLocation = Boolean(onLocationChange);
  const searchText = editableLocation ? location || '' : keyword;
  // 动态表单挂载时会把 undefined 同步为 null/空字符串，它们表示同一个空白地点。
  const longitudeValue = longitude ?? null; const latitudeValue = latitude ?? null; const locationValue = location ?? '';
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; version.current += 1; busyRef.current?.(false); };
  }, []);
  // 外部字段变化（手填、重置、切换状态）使未完成的地址/定位回调失效。
  useEffect(() => { version.current += 1; setBusy(''); busyRef.current?.(false); }, [longitudeValue, latitudeValue, locationValue, disabled]);
  const run = async (type, operation) => {
    const request = ++version.current;
    setBusy(type); setError(''); busyRef.current?.(true);
    const current = () => mounted.current && request === version.current;
    try { await operation(current); }
    catch (err) { if (current()) setError(err.message || '位置操作失败，请重试'); }
    finally { if (current()) { setBusy(''); busyRef.current?.(false); } }
  };
  const selectLocation = async (wgsPoint, name, current) => {
    // 逆地理编码不可用时仍返回点选坐标。
    let address = name;
    if (!address) {
      try { address = await reverseMapAddress(wgs84ToGcj02(wgsPoint)); }
      catch (err) { if (current()) setError(`${err.message}，仍可使用所选位置`); }
    }
    if (current()) {
      changeRef.current({ ...positionFields(wgsPoint), ...(address ? { location: address } : {}) });
      setPlaces([]); setSearched(false);
    }
  };
  const pick = (wgsPoint, name = '') => run('address', current => selectLocation(wgsPoint, name, current));
  const search = () => {
    const text = searchText.trim();
    if (!text) return;
    run('search', async current => {
      const result = await (currentPosition ? searchMapPlaces(text, currentPosition) : searchMapPlaces(text));
      if (current()) { setPlaces(result); setSearched(true); }
    });
  };
  const locate = (automatic = false) => run('locate', async current => {
    const position = await locateBrowser();
    if (!current()) return;
    setCurrentPosition(position);
    // 自动定位只补全空白地点，保留编辑中的地点及用户已有选点。
    const fields = fieldsRef.current;
    if (automatic && (fields.point || fields.location?.trim() || fields.disabled)) return;
    if (editableLocation) await selectLocation(position, '', current);
    else changeRef.current(positionFields(position), { automatic });
  });
  autoLocateRef.current = () => locate(true);
  useEffect(() => { if (autoLocate) autoLocateRef.current(); }, [autoLocate]);
  useEffect(() => {
    if (autoLocate && showMap && !mapShownRef.current) autoLocateRef.current();
    mapShownRef.current = showMap;
  }, [showMap, autoLocate]);
  const editLocation = event => {
    const value = event.target.value;
    version.current += 1; setPlaces([]); setSearched(false); setBusy(''); busyRef.current?.(false);
    if (editableLocation) {
      onLocationChange(value);
      // 修改地点后清除旧坐标，避免新地点名称沿用上一次选点的位置。
      if (longitude != null || latitude != null) changeRef.current({ longitude: null, latitude: null });
    } else setKeyword(value);
  };

  return <div className="location-picker">
    <div className="location-picker-toolbar">
      <Input.Search id={id} value={searchText} onChange={editLocation} maxLength={editableLocation ? 200 : undefined}
        placeholder={editableLocation ? '输入活动地点，或搜索学校、建筑及集合点' : '搜索学校、建筑或集合地点'} aria-label={editableLocation ? '活动地点' : '搜索地点'} onSearch={search} enterButton="搜索" loading={busy === 'search'} disabled={disabled} />
      <Button icon={<AimOutlined />} onClick={() => locate()} loading={busy === 'locate'} disabled={disabled || Boolean(busy)}>使用当前位置</Button>
    </div>
    {error && <Alert type="warning" showIcon title={error} />}
    {editableLocation && searchText.trim() && !point && busy !== 'address' && <Alert type="warning" showIcon title="活动地点尚未确认，请选择搜索结果、在地图上选点或使用当前位置。" />}
    {searched && <div className="location-picker-results">
      {places.length ? places.map(place => <button type="button" key={place.id} disabled={disabled || Boolean(busy)} onClick={() => pick(gcj02ToWgs84(place.point), place.name)}>
        <EnvironmentOutlined /><span><strong>{place.name}</strong><small>{place.address}</small></span>
      </button>) : <p>未找到相关地点，试试更完整的名称或在地图上选点。</p>}
    </div>}
    {showMap && <MapCanvas longitude={longitude} latitude={latitude} currentPosition={currentPosition} posts={posts} heatmap={heatmap} onPick={pick} disabled={disabled} />}
    <div className="location-picker-footer">
      {feedback}
      <p className="location-picker-hint">{busy === 'locate' ? '正在获取当前位置…' : busy === 'address' ? '正在获取地点名称…' : point ? '已选位置，可点击地图重新选择。' : '进入页面会自动尝试定位，也可搜索地点或点击地图选点。'}</p>
      {guidance && <p className="location-picker-hint">{guidance}</p>}
    </div>
  </div>;
};
const LocationPicker = props => props.compact ? <ActivityLocationPicker {...props} /> : <InlineLocationPicker {...props} />;
export default LocationPicker;
